import React from 'react';
import { FormControl, FormLabel, Select, Option } from '@mui/joy';

interface MeetingTimezoneSelectorProps {
  value: string;
  onChange: (timezone: string) => void;
}

// Function to detect browser timezone
const detectBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    return 'UTC';
  }
};

// Common timezones with their display names
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/London', label: 'London (GMT)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  { value: 'Europe/Moscow', label: 'Moscow (MSK)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Seoul', label: 'Seoul (KST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST)' },
];

const MeetingTimezoneSelector: React.FC<MeetingTimezoneSelectorProps> = ({ value, onChange }) => {
  const browserTimezone = detectBrowserTimezone();
  
  // If no timezone is selected, default to browser timezone
  React.useEffect(() => {
    if (!value && browserTimezone) {
      onChange(browserTimezone);
    }
  }, [value, browserTimezone, onChange]);

  return (
    <FormControl>
      <FormLabel sx={{ fontWeight: 500, fontSize: '1rem', color: '#1A1B1D', marginBottom: '8px' }}>
        Timezone
      </FormLabel>
      <Select
        value={value}
        onChange={(_, newValue) => onChange(newValue || '')}
        sx={{
          height: '56px',
          backgroundColor: '#ECEFF3',
          borderRadius: '12px',
          border: 'none',
          fontSize: '1.125rem',
          color: '#1A1B1D',
          '--Input-focusedHighlight': 'transparent !important',
          '&::placeholder': {
            color: '#7B7F85 !important',
            opacity: 1,
          },
          '&:hover': {
            backgroundColor: '#ECEFF3',
          },
          '&:focus': {
            outline: 'none',
          },
          '&:focus-within': {
            backgroundColor: '#ECEFF3',
          },
        }}
      >
        {TIMEZONES.map((timezone) => {
          const isCurrentTimezone = timezone.value === browserTimezone;
          return (
            <Option key={timezone.value} value={timezone.value}>
              {timezone.label}
              {isCurrentTimezone && ' (detected)'}
            </Option>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default MeetingTimezoneSelector; 